import URL from 'url';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import {Defaults, MEGA_BYTES, ORIGINAL, RGB_MAX_VAL} from './Constants'
import filter from 'lodash/filter';
import orderBy from 'lodash/orderBy';
import isArray from 'lodash/isArray';
import values from 'lodash/values';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import pick from 'lodash/pick';
import size from 'lodash/size';
import debounce from 'lodash/debounce';

export const fileSizeMB = (sizeInBytes) => {
    return (Math.round(sizeInBytes / MEGA_BYTES * 100) / 100) + ' MB';
};

export const fileDimensions = ({width, height, fullWidth, fullHeight}) => {
    return (fullWidth || width) + '×' + (fullHeight || height);
};

export const addParam = (url, param, value, skipUndefinedValues = true) => {
    const urlObj = URL.parse(url, true);
    // URL.format only considers the query if search is undefined
    delete urlObj.search;
    urlObj.query[param] = value;
    if (skipUndefinedValues) {
        urlObj.query = omitBy(urlObj.query, isUndefined);
    }
    return URL.format(urlObj);
};

export const addParams = (url, params, skipUndefinedValues = true) => {
    const urlObj = URL.parse(url, true);
    // URL.format only considers the query if search is undefined
    delete urlObj.search;
    Object.assign(urlObj.query, params);
    if (skipUndefinedValues) {
        urlObj.query = omitBy(urlObj.query, isUndefined);
    }
    return URL.format(urlObj);
};

export const getUrlQueryParam = (url, param) => {
    const urlObj = URL.parse(url, true);
    // URL.format only considers the query if search is undefined
    delete urlObj.search;
    return urlObj.query[param];
};

export const rgb2hsv = (r, g, b) => {

    r /= RGB_MAX_VAL;
    g /= RGB_MAX_VAL;
    b /= RGB_MAX_VAL;

    const v = Math.max(r, g, b);
    let h = 0;
    let s = (v - Math.min(r, g, b));


    const z = !s;
    s = s ? s : 1;
    if (r === v) {
        h = (g - b) / s;
    }
    if (g === v) {
        h = 2 + (b - r) / s;
    }
    if (b === v) {
        h = 4 + (r - g) / s;
    }
    h /= 6;

    if (h < 0) {
        h += 1;
    }
    h = z ? 0 : h;


    let tmp = s / v;
    tmp = z ? 0 : tmp;
    s = v ? tmp : s;
    s = !v ? 0 : s;

    // console.log("h,s,v: ",h,s,v);
    return [h, s, v];
};

export const hsv2rgb = (h, s, v) => {

    h *= 6.0;
    const k = Math.floor(h);
    let p = h - k;
    const t = 1 - s;
    const n = 1 - s * p;
    p = 1 - (s * (1 - p));

    let r = 0;
    let g = 0;
    let b = 0;

    if (k === 0 || k === 6) {
        r = 1;
        g = p;
        b = t;
    } else if (k === 1) {
        r = n;
        g = 1;
        b = t;
    } else if (k === 2) {
        r = t;
        g = 1;
        b = p;
    } else if (k === 3) {
        r = t;
        g = n;
        b = 1;
    } else if (k === 4) {
        r = p;
        g = t;
        b = 1;
    } else if (k === 5) {
        r = 1;
        g = t;
        b = n;
    }

    const f = RGB_MAX_VAL * v / Math.max(r, g, b);
    r *= f;
    g *= f;
    b *= f;

    // console.log("r, g, b: ",r,g,b);
    return [r, g, b];
};

export const hasVerticalScrollbar = (el = document.body) => {
    if (!el) {
        return false;
    }
    return el.scrollHeight > el.clientHeight;
};

let SCROLLBAR_WITH;

export const getScrollbarWith = () => {
    if (typeof SCROLLBAR_WITH !== 'undefined') {
        return SCROLLBAR_WITH;
    }
    if (!document) {
        return 0;
    }
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.width = '100px';
    outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps

    document.body.appendChild(outer);

    const widthNoScroll = outer.offsetWidth;
    // force scrollbars
    outer.style.overflow = 'scroll';

    // add innerdiv
    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);

    const widthWithScroll = inner.offsetWidth;

    // remove divs
    outer.parentNode.removeChild(outer);

    SCROLLBAR_WITH = widthNoScroll - widthWithScroll;
    return SCROLLBAR_WITH;
};

export const asLabel = (str) => {
    return str.replace(/([A-Z_])/g, ' $1')
        .replace(/^./, s => s.toUpperCase());
};

export const isValidNumber = (str) => {
    return !isNaN(str);
};

export const validateEmptyFields = (fields) => {
    const validationState = {};
    const helpMessage = {};
    map(fields, (value, key) => {
        if (!value) {
            validationState[key] = 'error';
            helpMessage[key] = asLabel(key) + ' is required';
        }
    });
    return {validationState, helpMessage};
};

export const prevIndex = (idx, sz) => {
    if (idx === null || typeof idx === 'undefined' || sz === 0) {
        return 0;
    }
    let prevIdx = idx || 0;
    if (prevIdx > 0) {
        --prevIdx;
    } else {
        prevIdx = sz - 1;
    }
    return prevIdx;
};

export const nextIndex = (idx, sz) => {
    if (idx === null || typeof idx === 'undefined' || sz === 0) {
        return 0;
    }
    let nextIdx = idx || 0;
    if (nextIdx < sz - 1) {
        ++nextIdx;
    } else {
        nextIdx = 0;
    }
    return nextIdx;
};

export const filterAndSortImages = (images, searchTerm, sortKey) => {
    if (searchTerm) {
        // eslint-disable-next-line no-param-reassign
        images = filter(images, img => img.fileName.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (!sortKey) {
        if (isArray(images)) {
            return images;
        }
        return values(images);
    }
    const sortArgs = sortKey.split('.');
    const field = sortArgs[0] || 'uploadTime';
    const dir = sortArgs[1] || 'desc';
    return orderBy(images, image => image[field], dir);
};

export const filterAndSortImageIds = (ids, images, searchTerm, sortKey) => {
    const sz = size(ids);
    if (sz <= 1) {
        return ids;
    }
    const selectedImages = pick(images, ids);
    const sortedImages = filterAndSortImages(selectedImages, searchTerm, sortKey);
    return map(sortedImages, 'id');
};

export const maybe = (...args) => {
    if (args.length === 2) {
        const value = args[0], defaultValue = args[1];
        return isNil(value) ? defaultValue : value;
    }
    for (let i = 0; i < args.length; ++i) {
        if (!isNil(args[i])) {
            return args[i];
        }
    }
    return null;
};

export const count = (collection, predicate = x => x) => {
    return sumBy(collection, item => predicate(item) ? 1 : 0);
};

export const extractErrorMessage = (e) => {
    if (isString(e)) {
        return e;
    }
    if (isEmpty(e)) {
        return null;
    }
    if (e.response) {
        if (isString(e.response)) {
            return e.response;
        }
        if (isString(e.response.message)) {
            return e.response.message;
        }
        let body;
        if (isObject(e.response.data)) {
            body = e.response.data;
        } else if (isObject(e.response.body)) {
            body = e.response.body;
        } else if (isString(e.response.text)) {
            try {
                body = JSON.parse(e.response.text);
            } catch (ex) {
                body = e.response;
            }
        }
        if (body) {
            if (isString(body.error)) {
                return body.error;
            }
            if (isString(body.message)) {
                return body.message;
            }
            if (isString(body.text)) {
                return body.text;
            }
        }
    } else if (e.message) {
        if (isString(e.message)) {
            return e.message;
        }
    }
    return JSON.stringify(e);
};

export const roundDown = (sz, prec = 10) => {
    return Math.floor(sz / prec) * prec;
};

export const getExtension = (fileName) => {
    if (!fileName) {
        return '';
    }
    const idx = fileName.lastIndexOf('.');
    if (idx <= 0) {
        return '';
    }
    return fileName.substring(idx + 1);
};

export const getExtensionAndBaseName = (fileName) => {
    if (!fileName) {
        return {baseName: '', extension: ''};
    }
    const idx = fileName.lastIndexOf('.');
    if (idx <= 0) {
        return {baseName: fileName, extension: ''};
    }
    return {baseName: fileName.substring(0, idx), extension: fileName.substring(idx + 1)};
};

export const fileNameAndType = ({activeImage, imageInfo, amplitude, spread, edited}, fileName = null, type = null) => {
    if (!fileName) {
        // eslint-disable-next-line no-param-reassign
        fileName = imageInfo.fileName;
    }
    const {baseName, extension} = getExtensionAndBaseName(fileName);
    if (!type) {
        // eslint-disable-next-line no-param-reassign
        type = extension.toLowerCase() || 'png';
        if (type !== 'png') {
            // eslint-disable-next-line no-param-reassign
            type = 'jpeg'; // we can't handle anything else yet
        }
    }
    const mimeType = 'image/' + type;
    const suffix = activeImage === ORIGINAL || isNil(amplitude) || isNil(spread) ? '' : `-a${amplitude}-s${spread}`;
    return {fileName: `${baseName}-dc-${activeImage}${suffix}${edited ? '-e' : ''}`, type, mimeType};
};

export const getPreferredSettings = (imageInfo, user) => {
    const preferredAmplitude = maybe(imageInfo.preferredAmplitude, user ? user.preferredAmplitude : null, Defaults.AMPLITUDE);
    const preferredSpread = maybe(imageInfo.preferredSpread, user ? user.preferredSpread : null, Defaults.SPATIAL_SCALE);
    return {preferredAmplitude, preferredSpread};
};

export const isPreferredSettings = ({amplitude, spread, preferredAmplitude, preferredSpread}) => {
    return (isNil(amplitude) || amplitude === preferredAmplitude) && (isNil(spread) || spread === preferredSpread);
};

export function debounceWithFirstCallArgs(func, wait, options) {
    let firstArgs, context;
    const wrapper = debounce(() => {
        const args = firstArgs;
        // eslint-disable-next-line no-undefined
        firstArgs = undefined;
        func.apply(context, args);
    }, wait, options);
    return function() {
        // eslint-disable-next-line consistent-this
        context = this;
        if (isUndefined(firstArgs)) {
            firstArgs = arguments;
        }
        wrapper();
    };
}



// WEBPACK FOOTER //
// ./src/utils.js